import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  isValidPhoneNumber,
  isValidPassword,
} from "@/utils/forgotPasswordUtils";
import PasswordStrengthIndicator from "./PasswordStrengthIndicator";

/**
 * 手机号密码重置表单
 * Vercel 风格设计，与 DomesticLoginPanel/DomesticRegisterPanel 保持一致
 *
 * 功能：
 * - 手机号输入和格式验证
 * - 发送验证码按钮（带60秒倒计时）
 * - 验证码输入
 * - 新密码输入（带确认）
 * - 提交重置请求
 * - 多语言支持
 *
 * Requirements: 1.2, 1.3, 1.4, 1.5, 4.1, 4.5, 5.1, 5.2, 5.3, 5.4
 */
export default function ForgotPasswordPhoneForm() {
  const { t } = useTranslation("login");
  const navigate = useNavigate();

  // 表单状态
  const [phone, setPhone] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // UI 状态
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  // 倒计时逻辑
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  /**
   * 获取 CSRF Token
   */
  const getCsrfToken = useCallback((): string => {
    return (
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.content || ""
    );
  }, []);

  /**
   * 发送验证码
   * Requirements: 1.2, 4.1, 5.1
   */
  const handleSendCode = async () => {
    // 验证手机号格式 (Requirement 1.4)
    if (!isValidPhoneNumber(phone)) {
      toast.error(t("forgotPasswordPhone.invalidPhone"));
      return;
    }

    setIsSendingCode(true);

    try {
      const response = await fetch("/users/send_password_reset_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(t("forgotPasswordPhone.codeSent"));
        setCountdown(60); // 60秒倒计时 (Requirement 4.1)
        setCodeSent(true);
      } else {
        toast.error(data.message || t("forgotPasswordPhone.sendFailed"));
      }
    } catch {
      toast.error(t("forgotPasswordPhone.networkError"));
    } finally {
      setIsSendingCode(false);
    }
  };

  /**
   * 提交密码重置
   * Requirements: 1.3, 1.5, 4.5, 5.2, 5.4
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证手机号格式 (Requirement 1.4)
    if (!isValidPhoneNumber(phone)) {
      toast.error(t("forgotPasswordPhone.invalidPhone"));
      return;
    }

    // 验证验证码
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error(t("forgotPasswordPhone.invalidCode"));
      return;
    }

    // 验证密码长度 (Requirement 4.5)
    if (!isValidPassword(newPassword)) {
      toast.error(t("forgotPasswordPhone.passwordTooShort"));
      return;
    }

    // 验证密码一致性
    if (newPassword !== confirmPassword) {
      toast.error(t("forgotPasswordPhone.passwordMismatch"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/password/verify_phone", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({
          phone,
          verification_code: verificationCode,
          password: newPassword,
          password_confirmation: confirmPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // 密码重置成功 (Requirement 5.2)
        toast.success(t("forgotPasswordPhone.resetSuccess"));
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        // 验证码错误或已过期 (Requirement 1.5)
        toast.error(data.error || t("forgotPasswordPhone.verifyFailed"));
        // 清空验证码输入框，允许重试
        setVerificationCode("");
      }
    } catch {
      toast.error(t("forgotPasswordPhone.networkError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 手机号输入 */}
      <div className="space-y-2">
        <Label htmlFor="phone">{t("forgotPasswordPhone.phoneLabel")}</Label>
        <div className="flex gap-2">
          <Input
            id="phone"
            type="tel"
            placeholder={t("forgotPasswordPhone.phonePlaceholder")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            autoFocus
            disabled={isLoading}
            className="flex-1 bg-background dark:bg-background"
          />
          <Button
            type="button"
            variant="outline"
            disabled={countdown > 0 || isSendingCode || isLoading}
            onClick={handleSendCode}
            className="shrink-0"
            data-testid="send-code-button"
          >
            {isSendingCode ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : countdown > 0 ? (
              `${countdown}s`
            ) : (
              t("forgotPasswordPhone.sendCode")
            )}
          </Button>
        </div>
      </div>

      {/* 验证码输入 */}
      <div className="space-y-2">
        <Label htmlFor="verification-code">
          {t("forgotPasswordPhone.codeLabel")}
        </Label>
        <Input
          id="verification-code"
          type="text"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          placeholder={t("forgotPasswordPhone.codePlaceholder")}
          value={verificationCode}
          onChange={(e) =>
            setVerificationCode(e.target.value.replace(/\D/g, ""))
          }
          required
          disabled={isLoading}
          className="bg-background dark:bg-background"
        />
      </div>

      {/* 新密码输入 */}
      <div className="space-y-2">
        <Label htmlFor="new-password">
          {t("forgotPasswordPhone.newPasswordLabel")}
        </Label>
        <Input
          id="new-password"
          type="password"
          placeholder={t("forgotPasswordPhone.newPasswordPlaceholder")}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={isLoading}
          aria-describedby="new-password-hint new-password-strength"
          className="bg-background dark:bg-background"
        />
        <PasswordStrengthIndicator
          password={newPassword}
          showCriteria={newPassword.length > 0}
          className="mt-2"
        />
      </div>

      {/* 确认密码输入 */}
      <div className="space-y-2">
        <Label htmlFor="confirm-password">
          {t("forgotPasswordPhone.confirmPasswordLabel")}
        </Label>
        <Input
          id="confirm-password"
          type="password"
          placeholder={t("forgotPasswordPhone.confirmPasswordPlaceholder")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
          className="bg-background dark:bg-background"
        />
      </div>

      {/* 提交按钮 (Requirement 5.4) */}
      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
        data-testid="submit-button"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("forgotPasswordPhone.submitting")}
          </>
        ) : (
          t("forgotPasswordPhone.submit")
        )}
      </Button>
    </form>
  );
}
